import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import MultiSelectDropdown from 'components/multiSelectDropdown';
import {
  MdClose,
  MdSearch,
  MdGroups,
  MdEventNote,
  MdPersonAddAlt1,
} from 'react-icons/md';
import toast from 'react-hot-toast';
import { AuthContext } from 'contexts/AuthContext';
import aiSessionService from 'ApiService/AiSession';
import api from 'ApiConfig';
import { WEEK_DAYS } from 'pages/helpers/sessionDays';
import { formatEnrollment } from 'pages/helpers/studentEnrollment';

const resolveLayout = (user) => {
  const hrRole = user?.hrRole;
  return `/${
    user?.role === 'placement'
      ? ['headhr', 'jrhr'].includes(hrRole)
        ? hrRole
        : 'placement'
      : user?.role
  }`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const toInputDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toISOString().slice(0, 10);
};

const initials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const Avatar = ({ name, tone }) => (
  <span
    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${tone}`}
  >
    {initials(name) || '?'}
  </span>
);

const CreateAiSession = () => {
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const isEditMode = Boolean(editId);
  const layout = resolveLayout(state?.user);

  const [form, setForm] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    daySchedules: [],
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSession, setLoadingSession] = useState(isEditMode);

  const [branches, setBranches] = useState([]);
  const [batchBranchFilter, setBatchBranchFilter] = useState('');

  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [loadingCourses, setLoadingCourses] = useState(false);

  const [batchStructures, setBatchStructures] = useState([]);
  const [selectedStructureId, setSelectedStructureId] = useState('');
  const [loadingStructures, setLoadingStructures] = useState(false);

  const [batches, setBatches] = useState([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingBatchStudents, setLoadingBatchStudents] = useState(false);

  useEffect(() => {
    fetchBranches();
    if (isEditMode) {
      fetchSessionForEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSessionForEdit = async () => {
    try {
      setLoadingSession(true);
      const response = await aiSessionService.getSessionById(editId);
      if (response.data.success) {
        const session = response.data.data;
        setForm({
          title: session.title || '',
          description: session.description || '',
          startDate: toInputDate(session.startDate),
          endDate: toInputDate(session.endDate),
          daySchedules: (session.daySchedules || []).map((slot) => ({
            day: slot.day,
            startTime: slot.startTime || '',
            endTime: slot.endTime || '',
          })),
        });
        setSelectedStudents(session.students || []);
      }
    } catch (error) {
      console.error('Error fetching AI session:', error);
      toast.error('Failed to load AI session');
    } finally {
      setLoadingSession(false);
    }
  };

  useEffect(() => {
    fetchCourses();
    setSelectedCourseId('');
    setSelectedStructureId('');
    setSelectedBatchIds([]);
    setBatchStructures([]);
    setBatches([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchBranchFilter]);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/combinedroutes/get-all-branches');
      if (response.data.success) {
        setBranches(response.data.branches || response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      setLoadingCourses(true);
      const response = await aiSessionService.listCourses(batchBranchFilter);
      if (response.data.success) {
        setCourses(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to fetch courses');
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleCourseSelect = async (e) => {
    const courseId = e.target.value;
    setSelectedCourseId(courseId);
    setSelectedStructureId('');
    setSelectedBatchIds([]);
    setBatchStructures([]);
    setBatches([]);
    if (!courseId) return;

    try {
      setLoadingStructures(true);
      const response = await aiSessionService.listBatchStructures(courseId);
      if (response.data.success) {
        setBatchStructures(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching batch structures:', error);
      toast.error('Failed to fetch batch structures');
    } finally {
      setLoadingStructures(false);
    }
  };

  const handleStructureSelect = async (e) => {
    const structureId = e.target.value;
    setSelectedStructureId(structureId);
    setSelectedBatchIds([]);
    setBatches([]);
    if (!structureId) return;

    try {
      setLoadingBatches(true);
      const response = await aiSessionService.listBatches(
        selectedCourseId,
        structureId,
        batchBranchFilter
      );
      if (response.data.success) {
        setBatches(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error('Failed to fetch batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleDay = (day) => {
    setForm((prev) => ({
      ...prev,
      daySchedules: prev.daySchedules.some((d) => d.day === day)
        ? prev.daySchedules.filter((d) => d.day !== day)
        : [...prev.daySchedules, { day, startTime: '', endTime: '' }],
    }));
  };

  const updateTime = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.daySchedules];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, daySchedules: updated };
    });
  };

  const handleSearch = async () => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      toast.error('Enter at least 2 characters to search');
      return;
    }
    try {
      setSearching(true);
      const response = await aiSessionService.searchStudents(
        searchTerm,
        batchBranchFilter ? [batchBranchFilter] : []
      );
      if (response.data.success) {
        setSearchResults(response.data.data || []);
      }
    } catch (error) {
      console.error('Error searching students:', error);
      toast.error('Failed to search students');
    } finally {
      setSearching(false);
    }
  };

  const handleBatchIdsChange = async (ids) => {
    setSelectedBatchIds(ids);
    if (ids.length === 0) {
      setSearchResults([]);
      return;
    }
    try {
      setLoadingBatchStudents(true);
      const responses = await Promise.all(
        ids.map((id) => aiSessionService.getBatchStudents(id))
      );
      const seen = new Set();
      const merged = [];
      responses.forEach((response) => {
        if (response.data.success) {
          (response.data.data || []).forEach((student) => {
            if (!seen.has(student._id)) {
              seen.add(student._id);
              merged.push(student);
            }
          });
        }
      });
      setSearchResults(merged);
      if (merged.length === 0) {
        toast.error('No active students found in the selected batch(es)');
      }
    } catch (error) {
      console.error('Error fetching batch students:', error);
      toast.error('Failed to fetch students for the selected batch(es)');
    } finally {
      setLoadingBatchStudents(false);
    }
  };

  const isSelected = (studentId) =>
    selectedStudents.some((s) => s._id === studentId);

  const addStudent = (student) => {
    if (isSelected(student._id)) return;
    setSelectedStudents((prev) => [...prev, student]);
  };

  const removeStudent = (studentId) => {
    setSelectedStudents((prev) => prev.filter((s) => s._id !== studentId));
  };

  const toggleStudent = (student) => {
    if (isSelected(student._id)) {
      removeStudent(student._id);
    } else {
      addStudent(student);
    }
  };

  const handleAddAll = () => {
    const newOnes = searchResults.filter((student) => !isSelected(student._id));
    if (newOnes.length === 0) {
      toast.error('All students from this list are already added');
      return;
    }
    setSelectedStudents((prev) => [...prev, ...newOnes]);
    toast.success(`Added ${newOnes.length} student(s) to the roster`);
  };

  const branchSummary = [
    ...new Set(
      selectedStudents
        .map((s) => s.branch?.name)
        .filter(Boolean)
    ),
  ].join(', ');

  const handleSubmit = async () => {
    if (!form.title || !form.startDate || !form.endDate) {
      toast.error('Title, start date and end date are required');
      return;
    }
    if (new Date(form.endDate) < new Date(form.startDate)) {
      toast.error('End date cannot be before start date');
      return;
    }
    if (form.daySchedules.length === 0) {
      toast.error('Select at least one week day');
      return;
    }
    if (
      form.daySchedules.some((d) => !d.startTime || !d.endTime)
    ) {
      toast.error('Set a start and end time for every selected week day');
      return;
    }
    if (selectedStudents.length === 0) {
      toast.error('Add at least one student to the roster');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...form,
        students: selectedStudents.map((s) => s._id),
      };
      const response = isEditMode
        ? await aiSessionService.updateSession({ id: editId, ...payload })
        : await aiSessionService.createSession(payload);
      if (response.data.success) {
        toast.success(
          isEditMode
            ? 'AI session updated successfully'
            : 'AI session created successfully'
        );
        const sessionId = isEditMode ? editId : response.data.data._id;
        navigate(`${layout}/ai-session/detail?id=${sessionId}`);
      }
    } catch (error) {
      console.error('Error saving AI session:', error);
      toast.error(
        error?.response?.data?.message ||
          `Failed to ${isEditMode ? 'update' : 'create'} AI session`
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingSession) {
    return (
      <Card extra="w-full pb-10 p-4">
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-500" />
        </div>
      </Card>
    );
  }

  return (
    <Card extra="w-full pb-10 p-4">
      <h2 className="mb-6 flex items-center gap-2 text-xl font-bold text-navy-700 dark:text-white">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-100 text-brand-500 dark:bg-navy-700">
          <MdGroups className="h-5 w-5" />
        </span>
        {isEditMode ? 'Edit AI Session' : 'Create AI Session'}
      </h2>

      <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-300">
        <MdEventNote className="h-4 w-4" />
        Session Details
      </h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <InputField
          label="Title"
          name="title"
          value={form.title}
          onChange={handleFormChange}
          placeholder="e.g. AI Tools Workshop"
        />
        <InputField
          label="Description"
          name="description"
          value={form.description}
          onChange={handleFormChange}
          placeholder="Optional description"
        />
        <div className="grid grid-cols-2 gap-4">
          <InputField
            label="From Date"
            type="date"
            name="startDate"
            value={form.startDate}
            onChange={handleFormChange}
          />
          <InputField
            label="To Date"
            type="date"
            name="endDate"
            value={form.endDate}
            onChange={handleFormChange}
          />
        </div>
        <div className="md:col-span-2">
          <label className="ml-1 text-sm font-bold text-navy-700 dark:text-white">
            Week Days
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            {WEEK_DAYS.map((day) => {
              const selected = form.daySchedules.some((d) => d.day === day);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(day)}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-all ${
                    selected
                      ? 'bg-brand-500 text-white shadow-md'
                      : 'border border-gray-200 bg-white/0 text-gray-600 hover:bg-brand-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {form.daySchedules.length > 0 && (
            <div className="mt-3 rounded-xl border border-gray-200 p-4 dark:border-white/10">
              <div className="mb-2 flex items-center gap-4 text-xs font-bold uppercase tracking-wide text-gray-500">
                <span className="w-28">Day</span>
                <span className="flex-1">Start Time</span>
                <span className="flex-1">End Time</span>
              </div>
              <div className="space-y-3">
                {form.daySchedules.map((item, idx) => (
                  <div key={item.day} className="flex items-center gap-4">
                    <span className="w-28 text-sm font-medium text-navy-700 dark:text-white">
                      {item.day}
                    </span>
                    <div className="flex-1">
                      <InputField
                        type="time"
                        value={item.startTime}
                        onChange={(e) =>
                          updateTime(idx, 'startTime', e.target.value)
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <InputField
                        type="time"
                        value={item.endTime}
                        onChange={(e) =>
                          updateTime(idx, 'endTime', e.target.value)
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <hr className="my-6 border-gray-200 dark:border-white/10" />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-lg font-bold text-navy-700 dark:text-white">
          <MdPersonAddAlt1 className="h-5 w-5" />
          Add Students to Roster
        </h3>
        {selectedStudents.length > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold text-brand-600 dark:bg-navy-700 dark:text-brand-300">
            <MdGroups className="h-4 w-4" />
            {selectedStudents.length} in roster
          </span>
        )}
      </div>
      <p className="mb-3 text-sm text-gray-500 dark:text-gray-300">
        Search individual students, or bulk-add everyone from a batch — from
        any batch, course, or branch.
      </p>

      <div className="mb-4 flex h-12 items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <input
            type="text"
            placeholder="Search by name / email / contact"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-12 w-full rounded-xl border border-gray-200 bg-white/0 p-3 pr-10 text-sm outline-none dark:!border-white/10 dark:text-white dark:placeholder:text-white/50"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setSearchResults([]);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-white"
            >
              <MdClose className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          onClick={handleSearch}
          disabled={searching}
          className="flex h-12 shrink-0 items-center justify-center gap-1 rounded-xl bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"
        >
          <MdSearch className="h-5 w-5" />
          {searching ? 'Searching...' : 'Search'}
        </button>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-2 rounded-xl border border-gray-200 p-3 dark:border-white/10">
        <div className="min-w-[160px]">
          <label className="ml-1 text-sm font-bold text-navy-700 dark:text-white">
            Branch
          </label>
          <select
            value={batchBranchFilter}
            onChange={(e) => setBatchBranchFilter(e.target.value)}
            className="mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:!border-white/10 dark:text-white"
          >
            <option value="">All Branches</option>
            {branches.map((branch) => (
              <option key={branch._id} value={branch._id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-[200px] flex-1">
          <label className="ml-1 text-sm font-bold text-navy-700 dark:text-white">
            Course
          </label>
          <select
            value={selectedCourseId}
            onChange={handleCourseSelect}
            disabled={loadingCourses}
            className="mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:!border-white/10 dark:text-white"
          >
            <option value="">
              {loadingCourses ? 'Loading courses...' : 'Select a course'}
            </option>
            {courses.map((course) => (
              <option key={course._id} value={course._id}>
                {course.courseName}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-[200px] flex-1">
          <label className="ml-1 text-sm font-bold text-navy-700 dark:text-white">
            Batch Structure
          </label>
          <select
            value={selectedStructureId}
            onChange={handleStructureSelect}
            disabled={!selectedCourseId || loadingStructures}
            className="mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:!border-white/10 dark:text-white disabled:opacity-50"
          >
            <option value="">
              {loadingStructures
                ? 'Loading structures...'
                : 'Select a batch structure'}
            </option>
            {batchStructures.map((structure) => (
              <option key={structure._id} value={structure._id}>
                {structure.name}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-[220px] flex-1">
          <label className="ml-1 text-sm font-bold text-navy-700 dark:text-white">
            Batch
          </label>
          <div className="mt-2">
            <MultiSelectDropdown
              options={batches.map((batch) => ({
                value: batch._id,
                label: `B${batch.batchNumber} · ${formatDate(
                  batch.startDate
                )} - ${formatDate(batch.endDate)}`,
              }))}
              selected={selectedBatchIds}
              onChange={handleBatchIdsChange}
              disabled={!selectedStructureId || loadingBatches}
              placeholder={
                loadingBatches ? 'Loading batches...' : 'Select batches'
              }
            />
          </div>
        </div>
        {loadingBatchStudents && (
          <p className="mb-3 text-xs text-gray-500">Loading students...</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-bold text-navy-700 dark:text-white">
              Results ({searchResults.length})
            </h4>
            {searchResults.length > 0 && (
              <button
                onClick={handleAddAll}
                className="rounded-lg bg-brand-500 px-3 py-1 text-xs font-medium text-white hover:bg-brand-600"
              >
                Add All
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10">
            {searchResults.length === 0 ? (
              <div className="flex flex-col items-center gap-1.5 p-6 text-center">
                <MdPersonAddAlt1 className="h-6 w-6 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500">
                  No results yet. Search above or select a batch.
                </p>
              </div>
            ) : (
              searchResults.map((student) => {
                const added = isSelected(student._id);
                return (
                  <label
                    key={student._id}
                    className={`flex cursor-pointer items-center justify-between gap-3 border-b border-gray-100 p-3 text-sm last:border-b-0 dark:border-white/5 ${
                      added ? 'bg-green-50 dark:bg-green-900/10' : 'hover:bg-gray-50 dark:hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={added}
                        onChange={() => toggleStudent(student)}
                      />
                      <Avatar
                        name={student.name}
                        tone={
                          added
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-navy-700 dark:text-gray-300'
                        }
                      />
                      <div>
                        <p className="font-medium text-navy-700 dark:text-white">
                          {student.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {student.email} · {student.branch?.name || 'No branch'}
                        </p>
                        {formatEnrollment(student) && (
                          <p className="text-xs text-gray-400">
                            {formatEnrollment(student)}
                          </p>
                        )}
                      </div>
                    </div>
                    {added && (
                      <span className="whitespace-nowrap rounded-full bg-green-200 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        Added
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>

        <div>
          <h4 className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
            Selected Roster ({selectedStudents.length})
          </h4>
          <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10">
            {selectedStudents.length === 0 ? (
              <div className="flex flex-col items-center gap-1.5 p-6 text-center">
                <MdGroups className="h-6 w-6 text-gray-300 dark:text-gray-600" />
                <p className="text-sm text-gray-500">No students added yet.</p>
              </div>
            ) : (
              selectedStudents.map((student) => (
                <div
                  key={student._id}
                  className="flex items-center justify-between gap-3 border-b border-gray-100 p-3 text-sm last:border-b-0 hover:bg-gray-50 dark:border-white/5 dark:hover:bg-white/5"
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={student.name}
                      tone="bg-brand-100 text-brand-600 dark:bg-navy-700 dark:text-brand-300"
                    />
                    <div>
                      <p className="font-medium text-navy-700 dark:text-white">
                        {student.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {student.branch?.name || 'No branch'}
                      </p>
                      {formatEnrollment(student) && (
                        <p className="text-xs text-gray-400">
                          {formatEnrollment(student)}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeStudent(student._id)}
                    className="rounded-full p-1 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                  >
                    <MdClose className="h-5 w-5" />
                  </button>
                </div>
              ))
            )}
          </div>
          {branchSummary && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {branchSummary.split(', ').map((name) => (
                <span
                  key={name}
                  className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-navy-700 dark:text-gray-300"
                >
                  {name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 flex justify-end gap-3">
        <button
          onClick={() => navigate(`${layout}/ai-session`)}
          className="rounded-xl border border-gray-300 px-5 py-2 text-sm font-medium text-navy-700 dark:border-white/10 dark:text-white"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="rounded-xl bg-brand-500 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-600 disabled:opacity-60"
        >
          {submitting
            ? isEditMode
              ? 'Saving...'
              : 'Creating...'
            : `${isEditMode ? 'Save Changes' : 'Create Session'}${
                selectedStudents.length
                  ? ` (${selectedStudents.length} students)`
                  : ''
              }`}
        </button>
      </div>
    </Card>
  );
};

export default CreateAiSession;
